import Services from "../Services/Services";
import { orgImageTypes } from "./orgImageTypes";
import { securePostMessage, validateInput } from "./security";

const isDev = process.env.NODE_ENV === "development";

// Debug logging utility - only logs in development
export const debugLog = (...args) => {
  if (isDev) {
    console.log(...args);
  }
};

// Strip [PAGE_CONTEXT]...[/PAGE_CONTEXT] tags from message content
export const stripPageContext = (text) => {
  if (!text || typeof text !== "string") return text;
  return text.replace(/\[PAGE_CONTEXT\][\s\S]*?\[\/PAGE_CONTEXT\]\s*/g, "").trim();
};

// Shared AudioContext for TTS — once unlocked by a user gesture, stays unlocked
let sharedAudioCtx = null;
export async function getAudioContext() {
  if (!sharedAudioCtx) {
    sharedAudioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (sharedAudioCtx.state === "suspended") {
    await sharedAudioCtx.resume();
  }
  return sharedAudioCtx;
}

// Clean message text for TTS playback — strips HTML, URLs, markers
export const cleanTextForTTS = (text) => {
  if (!text || typeof text !== "string") return "";
  return stripPageContext(text)
    .replace(/<[^>]*>/g, "")
    .replace(/https?:\/\/[^\s]+/g, "")
    .replace(/\[TRANSFER_TO_HUMAN\]/g, "")
    // Strip emojis so TTS doesn't read "smiley face", "winking face" etc.
    .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{FE00}-\u{FE0F}\u{200D}\u{2B50}\u{2B55}\u{203C}-\u{3299}\u{1F3FB}-\u{1F3FF}]/gu, "")
    .replace(/\s{2,}/g, " ")
    .trim();
};

const baseUrl = process.env.REACT_APP_BACKEND_BASE_URL
  ? process.env.REACT_APP_BACKEND_BASE_URL
  : "https://api-yeshtery.dev.meetusvr.com/v1";

const full360Url =
  process.env.REACT_APP_360_BASE_URL ||
  process.env.REACT_APP_FILES_BASE_URL + "/shop360/pannellum/v3/index.html" ||
  "https://www.dev.meetusvr.com/files/shop360/pannellum/v3/index.html";

const get360Url = ({ shopId, productId }) => {
  const shop = shopId ? `&shop_id=${shopId}` : "";
  const product = productId ? `&product_id=${productId}` : "";
  return `${full360Url}?autoLoad=true${shop}${product}`;
};

const defaultChatSettings = (orgData) => {
  return {
    mode: "light",
    color: orgData.theme?.settings?.colors?.primary || "#000",
    title: `Explore ${orgData?.name}`,
    welcoming: "Hi, what can I help you with?",
    images: [],
  };
};

function hexToRgb(hex, alpha) {
  hex = hex.replace("#", "");
  const r = parseInt(
    hex.length === 3 ? hex.slice(0, 1).repeat(2) : hex.slice(0, 2),
    16
  );
  const g = parseInt(
    hex.length === 3 ? hex.slice(1, 2).repeat(2) : hex.slice(2, 4),
    16
  );
  const b = parseInt(
    hex.length === 3 ? hex.slice(2, 3).repeat(2) : hex.slice(4, 6),
    16
  );
  if (alpha) {
    return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
  } else {
    return r + ", " + g + ", " + b;
  }
}

// Relative luminance (WCAG) of a brand color. Handles 3/6/8-digit hex (alpha ignored).
function brandLuminance(hex) {
  const [r, g, b] = hexToRgb(hex).split(",").map((n) => parseInt(n, 10));
  const toLin = (c) => {
    const s = c / 255;
    return s <= 0.03928 ? s / 12.92 : Math.pow((s + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * toLin(r) + 0.7152 * toLin(g) + 0.0722 * toLin(b);
}

// WCAG contrast ratio between two luminances.
function contrastRatio(l1, l2) {
  const [light, dark] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (light + 0.05) / (dark + 0.05);
}

// Pick whichever text color (dark or white) has the higher contrast ratio
// against the brand background — works correctly for all hues and mid-tones.
function getContrastText(hex) {
  const bgLum = brandLuminance(hex);
  const darkText = "#1a1a1a";
  const darkLum = brandLuminance(darkText);
  return contrastRatio(bgLum, darkLum) >= contrastRatio(bgLum, 1)
    ? darkText
    : "#ffffff";
}

export const getDataHandling = async ({ orgId, preview, dashboardToken }) => {
  const [{ data: orgData }, { data: settingsData }, { data: shopsData }] =
    await Promise.all([
      Services.getOrganization({ orgId: orgId }),
      Services.getChatSettings({
        orgId: orgId,
        type: preview ? "get-unpublished" : "get-published",
        dashboardToken,
      }),
      Services.getShops(orgId),
    ]);

  let chatSettingsData = JSON.parse(settingsData?.value);

  if (!chatSettingsData) {
    chatSettingsData = defaultChatSettings(orgData);
  }

  const shopHas360 = shopsData?.content?.find((item) => {
    if (item?.has_360) return item;
    else return null;
  });

  return { orgData, chatSettingsData, shopsData, shopHas360 };
};

export const handleChatImages = ({ chatSettingsData, orgData }) => {
  chatSettingsData?.images?.forEach((img) => {
    if (orgImageTypes[img?.type]) {
      if (orgImageTypes[img?.type] === orgImageTypes["332"]) {
        chatSettingsData.images[orgImageTypes[img?.type]] = [
          ...(chatSettingsData.images[orgImageTypes[img?.type]] || []),
          img?.image_url,
        ];
      } else {
        chatSettingsData.images[orgImageTypes[img?.type]] = img?.image_url;
      }
    }
  });

  if (!chatSettingsData?.images) {
    chatSettingsData.images = [];
  }

  /**
   * Convert orgData.images array into a dictionary of filtered image types.
   *
   * @param {Object} orgData - The organization data object.
   * @returns {Object} orgImagesDict - A dictionary of image URLs, where the keys are the image types.
   */
  const orgImagesDict = orgData?.images
    ?.filter((im) => orgImageTypes.hasOwnProperty(im?.type))
    ?.reduce((dict, im) => {
      return { ...dict, [im.type]: im.url };
    }, {});

  chatSettingsData.images["logo_url"] =
    chatSettingsData.images["logo_url"] || orgImagesDict?.[1];

  chatSettingsData.images["chat_services_widget_background"] =
    chatSettingsData.images["chat_services_widget_background"] ||
    orgImagesDict?.[301];

  return chatSettingsData;
};

export const getOriginUrlAndOrgId = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const orgId = urlParams.get("orgId");
  const preview = urlParams.get("preview");
  const originUrl = urlParams.get("url") || "*";
  const dashboardToken = urlParams.get("dashboardToken");
  return {
    orgId,
    originUrl,
    preview: preview === "true",
    dashboardToken,
  };
};

export const postMessage = (message) => {
  const { originUrl } = getOriginUrlAndOrgId();
  let targetOrigin = "*";
  if (originUrl && originUrl !== "*") {
    try {
      targetOrigin = new URL(originUrl).origin;
    } catch (e) {
      // Malformed URL param, fall back to wildcard
    }
  }
  securePostMessage(message, targetOrigin);
};

export const isArabicText = (text) => /[\u0600-\u06FF]/.test(text);

export const getImageByType = (imageList, type) =>
  imageList?.find((image) => image?.type === type)?.image_url;

/**
 * Separates messages into customer and agent messages
 * @param {Array} messages - Array of message objects
 * @returns {Object} Object containing separated messages and counts
 */
export const separateMessages = (messages) => {
  if (!Array.isArray(messages) || messages.length === 0) {
    return {
      customerMessages: [],
      agentMessages: [],
      botMessages: [],
      totalCount: 0,
      customerCount: 0,
      agentCount: 0,
      botCount: 0,
    };
  }

  const customerMessages = [];
  const agentMessages = [];
  const botMessages = [];

  messages.forEach((message) => {
    if (!message || typeof message !== 'object') return;

    // Validate and sanitize message content
    if (message.content && typeof message.content === 'string') {
      message.content = validateInput(message.content, { maxLength: 5000 });
    }

    const senderType =
      message.senderType || (message.sender?.agent ? "AGENT" : "USER") || "BOT";

    switch (senderType) {
      case "AGENT":
        agentMessages.push(message);
        break;
      case "USER":
        customerMessages.push(message);
        break;
      case "BOT":
        botMessages.push(message);
        break;
      default:
        // Default to customer if type is unknown
        customerMessages.push(message);
    }
  });

  return {
    customerMessages,
    agentMessages,
    botMessages,
    totalCount: messages.length,
    customerCount: customerMessages.length,
    agentCount: agentMessages.length,
    botCount: botMessages.length,
  };
};

const imageExtensions = [".png", ".jpg", ".svg"];
const modelExtensions = [".gltf", ".glb"];

export const imageUrls = (urls) =>
  urls?.filter((url) =>
    imageExtensions.some((extension) => url.endsWith(extension))
  );

export const modelUrls = (urls) =>
  urls?.filter((url) =>
    modelExtensions.some((extension) => url.endsWith(extension))
  );

const Utils = {
  set360Cookies(userToken) {
    const cookieData = {
      backend_api_url: baseUrl,
      files_api_url: isDev ? `${baseUrl}/files` : "/files",
    };
    if (userToken) {
      cookieData.user_token = userToken;
    }
    document.cookie = `360=${JSON.stringify(cookieData)}; path=/; domain=${
      window.location.host
    };`;
  },
  getProper360Url(shopId) {
    return get360Url({ shopId });
  },
  getProperProduct360Url(shopId, productId) {
    return get360Url({ shopId, productId });
  },
  getProperImageUrl(imagePath) {
    return `${baseUrl}/files/${imagePath}`;
  },
  getProductPrices(product) {
    const { price, discount } = product;
    const sale = Number(price - price * ((discount || 0) / 100));
    return { sale, original: price };
  },
  getProductPricesDetails(product) {
    const { price, discount } = product;
    const sale = Number(price - discount);
    return { sale, original: price };
  },
  calculateDiscountPercentage(price, discount) {
    return (discount / price) * 100;
  },
  isNewProduct(creationDate) {
    return new Date().getTime() === new Date(creationDate).getTime();
  },
  setAppColors(themeSettings) {
    const { colors = {} } = themeSettings;
    Object.entries(colors || {}).forEach(([color, value]) => {
      document.documentElement.style.setProperty(
        "--" + color.replace("_", "-") + "-color", // eg: --primary-color
        value
      );
      document.documentElement.style.setProperty(
        "--" + color.replace("_", "-") + "-color-rgb", // eg: --primary-color-rgb
        hexToRgb(value)
      );
    });
  },
  setWidgetColors(color) {
    const safeColor =
      typeof color === "string" &&
      /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})$/.test(color)
        ? color
        : "#019AA6";

    document.documentElement.style.setProperty("--primary-color", safeColor);
    document.documentElement.style.setProperty(
      "--primary-color-rgb",
      hexToRgb(safeColor)
    );
    // Also set chat-widget-bg for SideServicesApp (floating widget)
    document.documentElement.style.setProperty("--chat-widget-bg", safeColor);
    // Readable text color on brand-colored surfaces (fixes white-on-white when brand is light)
    document.documentElement.style.setProperty(
      "--primary-text",
      getContrastText(safeColor)
    );
    // Subtle border so a near-white brand button keeps an edge against a light bg
    document.documentElement.style.setProperty(
      "--primary-border",
      brandLuminance(safeColor) > 0.85 ? "rgba(0, 0, 0, 0.12)" : "transparent"
    );
  },
};

export default Utils;
